import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { OverviewProjectService } from 'src/app/services/overview-project.service';
import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ToastService } from '../../toast/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'fun-overvier-calculator',
  templateUrl: './overvier-calculator.component.html',
  styleUrls: ['./overvier-calculator.component.scss'],
})
export class OvervierCalculatorComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() id: any;
  @Input() profitability: any;
  @Input() investors: any;
  @Input() info: any;
  @Input() type: any;
  @Input() status: number;
  @Input() isFlexibleCompensation: boolean = false;
  @Output() public openInvestEvent = new EventEmitter<any>();
  @Output() public calculateHubspotEvent = new EventEmitter<any>();

  calendarInfo: any;
  partialCalendar: any;
  hasPartialCalendar: boolean;
  benefits: any;
  hasInfo = false;
  loadingCalendarInfo = false;
  originalValue: any;
  inputType = this.isFlexibleCompensation ? 'secondary' : 'primaryDark';
  period: any;

  public cashForm: UntypedFormGroup;
  public formControlKeys = [];
  public errorMessages = {};
  public isSubmit = false;
  public errorLogin = false;

  hasLiveCapital = false;

  // Calendario por clase de participación (FUN-892): con dos o más clases se elige cuál calcular, por defecto la más rentable.
  // Sin calculatorClasses (calendario global o back anterior) la calculadora funciona como siempre.
  calculatorClasses: any[] = [];
  selectedClass: any = null;
  private lastRequest = 0;

  constructor(
    private overviewService: OverviewProjectService,
    private toastService: ToastService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    registerLocaleData(es);
    this.loadCalculatorClasses();
    this.getCalendarInfo('5000');
    this.cashForm = new UntypedFormGroup({
      money: new UntypedFormControl('', [])
    });
    this.cashForm.patchValue({ money: '5000' });
    if (this.info !== undefined) {
      this.benefits = this.oldCalculator(5000)
    } else {
      const bnf = {
        reem: 0,
        inversion: 0,
        total: 0
      }
      this.benefits = bnf;
    }
    // console.log(this.info, '--info--')
  }

  ngAfterViewInit() {
    this.cashForm.patchValue({ money: '5.000' });
    setTimeout(() => this.calculate(false), 1000);
  }

  calculate(byInput) {
    this.originalValue = this.cashForm.value.money.replace(/\D/g, '');
    if (Number(this.originalValue) >= 500) {
      this.loadingCalendarInfo = true;
      const valueCopy = this.originalValue;
      this.inputType = this.isFlexibleCompensation ? 'secondary-disabled' : 'primaryDark-disabled';
      setTimeout(() => {
        if (this.originalValue === valueCopy && this.originalValue !== '') {
          this.getCalendarInfo(this.cashForm.value.money.replace(/\D/g, ''));
          if (byInput) {
            this.calculateHubspotEvent.emit({ amount: this.cashForm.value.money.replace(/\D/g, '') });
          }
        } else {
          this.loadingCalendarInfo = true;
        }
        this.inputType = this.isFlexibleCompensation ? 'secondary' : 'primaryDark';
      }, 500);
    } else {
      this.toastService.showToast(this.translate.instant('INVEST.MIN_INVEST_ALLOWED'), 'info', true);
    }
  }

  // El detalle pinta la calculadora antes de tener los datos del proyecto (info llega después, de forma asíncrona)
  ngOnChanges(changes: SimpleChanges) {
    if (changes.info && !changes.info.firstChange && this.loadCalculatorClasses()) {
      this.hasInfo = false;
      this.calendarInfo = null;
      this.partialCalendar = null;
      this.hasPartialCalendar = undefined;
      this.loadingCalendarInfo = true;
      const money = String(this.cashForm?.value?.money || '').replace(/\D/g, '');
      this.getCalendarInfo(Number(money) >= 500 ? money : '5000');
    }
  }

  /** Lee las clases de info; devuelve true si han cambiado (hay que recalcular) */
  private loadCalculatorClasses(): boolean {
    const classes = (this.info?.calculatorClasses || []).slice()
      .sort((a, b) => (b.profitability || 0) - (a.profitability || 0));
    const ids = classes.map(c => c.id).join(',');
    if (ids === this.calculatorClasses.map(c => c.id).join(',')) {
      return false;
    }
    this.calculatorClasses = classes;
    this.selectedClass = classes.length > 1 ? classes[0] : null;
    return true;
  }

  get hasClassSelector(): boolean {
    return this.calculatorClasses.length > 1;
  }

  get classSelectorLabel(): string {
    const key = 'OVERVIEW_PROJECT.CALCULATOR_CLASS';
    const text = this.translate.instant(key);
    return text && text !== key ? text : 'Tipo de participación';
  }

  get shownProfitability() {
    return this.selectedClass && this.selectedClass.profitability != null ? this.selectedClass.profitability : this.profitability;
  }

  /** Con clase elegida, lo que devuelve su calendario por euro; si no, el multiplicador del proyecto */
  private get calculatorMultiplier() {
    return (this.selectedClass && this.selectedClass.calculator_multiplier) || this.info.revenueCalculatorMultiplier;
  }

  selectClass(calculatorClass) {
    if (this.selectedClass?.id === calculatorClass.id) {
      return;
    }
    this.selectedClass = calculatorClass;
    this.hasInfo = false;
    this.calendarInfo = null;
    this.partialCalendar = null;
    this.hasPartialCalendar = undefined;
    this.loadingCalendarInfo = true;
    const money = String(this.cashForm.value.money || '').replace(/\D/g, '');
    this.getCalendarInfo(Number(money) >= 500 ? money : '5000');
  }

  getCalendarInfo(value) {
    const params: any = {
      projectId: this.id,
      amount: value
    }
    if (this.selectedClass) {
      params.participationId = this.selectedClass.id;
    }
    // Si cambian importe o clase antes de que llegue la respuesta, solo vale la última petición
    const requestId = ++this.lastRequest;
    this.overviewService.getCalendarInfo(params).subscribe(resp => {
      if (requestId !== this.lastRequest) {
        return;
      }
      const calendarGroupedByYear = this.groupByYears(resp);
      const new_bnf = this.getBenefits(calendarGroupedByYear, value);
      const old_bnf = this.oldCalculator(Number(this.cashForm.value.money.replace(/\D/g, '')));
      const totalByoldCalculator = this.getOldCalculatorOperation(value);
      if (
        (Number(new_bnf['total']) - (Number(new_bnf['total']) * 0.02)) <= totalByoldCalculator.total &&
        (Number(new_bnf['total']) + (Number(new_bnf['total']) * 0.02)) >= totalByoldCalculator.total
      ) {
        // Un calendario por clase suele ocupar un año natural más que el plazo (p. ej. dic-2025 a oct-2030 en 5 años)
        if (calendarGroupedByYear.length === this.info.term || (this.selectedClass && calendarGroupedByYear.length === this.info.term + 1)) {
          this.hasInfo = true;
          this.calendarInfo = this.getPeriod(resp); // calendarGroupedByYear;
          this.benefits = this.getBenefits(calendarGroupedByYear, value);
          if (this.calendarInfo.length > 15) {
            this.partialCalendar = {
              prev: [this.calendarInfo[0], this.calendarInfo[1], this.calendarInfo[2]],
              next: [this.calendarInfo[this.calendarInfo.length - 3], this.calendarInfo[this.calendarInfo.length - 2], this.calendarInfo[this.calendarInfo.length - 1]]
            }

            if (this.hasPartialCalendar !== false) {
              this.hasPartialCalendar = true;
            }
          }
          //
          this.loadingCalendarInfo = false;
        } else {
          this.benefits = this.oldCalculator(Number(this.cashForm.value.money.replace(/\D/g, '')));
          this.loadingCalendarInfo = false;
        }
      } else {
        this.benefits = this.oldCalculator(Number(this.cashForm.value.money.replace(/\D/g, '')));
        this.loadingCalendarInfo = false;
      }
    }, error => {
      if (requestId !== this.lastRequest) {
        return;
      }
      console.log(error)
      this.benefits = this.oldCalculator(Number(this.cashForm.value.money.replace(/\D/g, '')));
      this.loadingCalendarInfo = false;
    });
  }

  getBenefits(calend, amountSelected) {
    const amountToCalculate = Number(amountSelected)
    //this.type
    if (calend !== null) {
      const amount = calend.reduce((accumulator, object) => {
        return accumulator + object.amount;
      }, 0);
      const bnf = {
        reem: amount - amountToCalculate,
        inversion: amountToCalculate,
        total: amount //+ amountToCalculate
      }
      return bnf;
    }
  }

  invest() {
    this.openInvestEvent.emit('invest');
  }

  viewAll() {
    this.hasPartialCalendar = false;
  }

  oldCalculator(valueMoney) {
    let profit;
    if (valueMoney && valueMoney !== 0) {
      if (this.calculatorMultiplier) {
        profit = valueMoney * this.calculatorMultiplier;
      } else {
        profit = 0;
      }
    } else {
      profit = 0;
    }
    const bnf = {
      reem: profit - valueMoney,
      inversion: valueMoney,
      total: profit
    }
    return bnf;
  }

  getOldCalculatorOperation(valueMoney) {
    let profit;
    if (Number(valueMoney) && Number(valueMoney) !== 0) {
      if (this.calculatorMultiplier) {
        profit = Number(valueMoney) * this.calculatorMultiplier;
      } else {
        if (this.info.totalProfit && this.info.totalProfit !== 0) {
          profit = (Number(valueMoney) / this.info.total) * this.info.totalProfit;
        } else {
          profit = 0;
        }
      }
    } else {
      profit = 0;
    }
    const bnf = {
      value: Number(valueMoney),
      reem: profit - Number(valueMoney),
      inversion: Number(valueMoney),
      total: profit
    }
    return bnf;
  }

  groupByYears(resp) {
    let yearList = [];
    let paymentsByYear = [];
    for (let i = 0; i < resp.length; i++) {
      if (yearList.length === 0 || resp[i]['date'].substring(6, 10) !== yearList[yearList.length - 1]) {
        yearList.push(resp[i]['date'].substring(6, 10));
      }
    }
    for (let i = 0; i < yearList.length; i++) {
      const payment = resp.filter((item) => item['date'].substring(6, 10) === yearList[i]);
      const date = payment[0]['date'];
      let amount = 0;
      let profitOrInterest = 0;
      let sharePremiumOrRedemption = 0;
      let liveCapital = 0;
      for (let j = 0; j < payment.length; j++) {
        amount = amount + payment[j]['amount'];
        profitOrInterest = profitOrInterest + payment[j]['profitOrInterest'];
        sharePremiumOrRedemption = sharePremiumOrRedemption + payment[j]['sharePremiumOrRedemption'];
        if (payment[j]['liveCapital'] !== null) {
          let liveCapital = payment[j]['liveCapital'];
          if (liveCapital > 0) {
            this.hasLiveCapital = true;
          }
        }
      }
      const obj = {
        'date': date,
        'amount': amount,
        'profitOrInterest': profitOrInterest,
        'sharePremiumOrRedemption': sharePremiumOrRedemption,
        'liveCapital': liveCapital
      }
      paymentsByYear.push(obj);
      amount = 0;
      profitOrInterest = 0;
      sharePremiumOrRedemption = 0;
      liveCapital = 0;
    }
    return paymentsByYear;
  }

  getPeriod(objetList) {
    // Crear un objet para almacenar los objets agrupados por year
    const periodByYear = {};

    objetList.forEach(objet => {
      const year = objet.date.split('/')[2]; // Obtener el year del objet
      if (!periodByYear[year]) {
        periodByYear[year] = [objet];
      } else {
        periodByYear[year].push(objet);
      }
    });

    // Asignar el período correspondiente a cada grupo de objets por year
    let index = 0;
    for (const year in periodByYear) {
      const periodTime = periodByYear[year].length;
      this.period = this.getPeriodTime(periodTime);

      periodByYear[year].forEach(objet => {
        index += 1;
        objet.period = periodTime !== 6 ? this.period + ' ' + index : this.translate.instant('CALENDAR.MONTH') + ' ' + (index * 2);
      });
    }

    return objetList;
  }

  getPeriodTime(periodTime) {
    let period;

    switch (periodTime) {
      case 1:
        period = this.translate.instant('CALENDAR.YEAR');
        break;
      case 2:
        period = this.translate.instant('CALENDAR.SEMESTER');
        break;
      case 3:
        period = this.translate.instant('CALENDAR.FOUR_MONTH');
        break;
      case 4:
        period = this.translate.instant('CALENDAR.TRIMESTER');
        break;
      case 6:
        period = this.translate.instant('CALENBDAR.BIMESTER');
        break;
      case 12:
        period = this.translate.instant('CALENDAR.MONTH');
        break;
      default:
        period = this.translate.instant('CALENDAR.YEAR');
        break;
    }
    if (this.id == 130) {
      period = this.translate.instant('CALENBDAR.BIMESTER');
    }
    return period;
  }

}
